import { isFunction } from 'lodash';
import { assign, createMachine, sendParent } from 'xstate';
import { isPowerSetter } from '~/funnels/powersetter/machine';
import { setError } from '~/machines/utils/actions';
import { LDClient, LDFlags } from '~/providers';
import {
  createUser,
  fetchFunnelPacket,
  updateConsolidatedUtilityAccount,
  updateUserPreferences,
} from '~/services';
import { CreateUserParams } from '~/services/create-user';
import { FunnelTrack } from '~/typings/graphql';
import { PROMO_CODES, UTILITY_IDS } from '~/utils/constants';
import { store } from '~/utils/store';
import {
  allowConsolidatedUtilityAccount,
  getUserIpFromStore,
  identify,
  sendToSegment,
} from '~/utils/tracking';
import { contextCreator } from '../utils/context';
import { UserContext, UserEvent, UserServices, UserStates, UserDoneData } from './types';

export const userMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as UserContext,
        events: {} as UserEvent,
        services: {} as UserServices,
      },
      context: {
        errors: {},
        requireBusinessName: false,
        requirePhone: false,
        showPhone: false,
        showInviteCode: false,
        forceWaitlist: false,
      } as UserContext,
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: [
            { cond: 'shouldViewMicroLearning', target: 'microLearning' },
            { cond: 'hasReturnedFromGoogle', target: 'google' },
            { target: 'user' },
          ],
        },
        microLearning: {
          id: 'microLearning',
          initial: 'getStarted',
          states: {
            getStarted: {
              id: 'getStarted',
              entry: 'trackScreen1',
              on: {
                NEXT: {
                  target: 'solarWithoutARoof',
                  actions: 'trackNextButtonPressedScreen1',
                },
                SKIP: {
                  target: 'completed',
                  actions: 'trackSkipButtonPressedScreen1',
                },
              },
            },
            solarWithoutARoof: {
              id: 'solarWithoutARoof',
              entry: 'trackScreen2',
              on: {
                NEXT: {
                  target: 'howItWorks',
                  actions: 'trackNextButtonPressedScreen2',
                },
                SKIP: {
                  target: 'completed',
                  actions: 'trackSkipButtonPressedScreen2',
                },
              },
            },
            howItWorks: {
              id: 'PowerfulClimateAction',
              entry: 'trackScreen3',
              on: {
                NEXT: {
                  target: 'readyToSubscribe',
                  actions: 'trackNextButtonPressedScreen3',
                },
                SKIP: {
                  target: 'completed',
                  actions: 'trackSkipButtonPressedScreen3',
                },
              },
            },
            readyToSubscribe: {
              id: 'readyToSubscribe',
              entry: 'trackScreen4',
              on: {
                SKIP: {
                  target: 'completed',
                  actions: 'trackSkipButtonPressedScreen4',
                },
                DONE: {
                  target: 'completed',
                  actions: 'trackDoneButtonPressedScreen4',
                },
              },
            },
            completed: {
              id: 'completed',
              always: [{ target: '#user' }],
            },
          },
        },
        user: {
          id: 'user',
          initial: 'idle',
          states: {
            idle: {
              on: {
                CHANGE_UTILITY: { actions: 'changeUtility' },
                SUBMIT_USER: {
                  target: 'submitting',
                  actions: ['saveUserSubmission', 'saveStripePaymentIds'],
                },
              },
            },
            submitting: {
              entry: 'clearUserError',
              invoke: {
                id: 'createUser',
                src: 'createUser',
                onDone: { target: '#completed', actions: ['saveFunnelPacket', 'trackOptInToSms'] },
                onError: { target: 'idle', actions: 'setUserError' },
              },
            },
          },
        },
        google: {
          id: 'google',
          initial: 'idle',
          states: {
            idle: {
              on: {
                CHANGE_UTILITY: { actions: 'changeUtility' },
                SUBMIT_USER: { target: 'submitting', actions: 'saveUserSubmission' },
              },
            },
            submitting: {
              entry: 'clearGoogleError',
              invoke: {
                id: 'createUser',
                src: 'createUser',
                onDone: { target: '#completed', actions: ['saveFunnelPacket', 'trackOptInToSms'] },
                onError: { target: 'idle', actions: 'setGoogleError' },
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: ['trackCompleted', 'identifyUserForTracking'],
          invoke: {
            id: 'updateFunnelStatus',
            src: 'updateFunnelStatus',
          },
          data: (context): UserDoneData => ({
            funnelPacket: context.funnelPacket!,
            utmParams: context.utmParams,
          }),
        },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        changeUtility: sendParent({ type: 'CHANGE_UTILITY' }),
        clearUserError: assign(context => ({
          errors: { ...context.errors, user: undefined },
        })),
        clearGoogleError: assign(context => ({
          errors: { ...context.errors, google: undefined },
        })),
        identifyUserForTracking: context => {
          identify({
            id: context.funnelPacket?.user.id!,
            email: context.funnelPacket?.user.email!,
            utilityId: context.funnelPacket?.utility.id!,
            funnelTrack: context.forceWaitlist
              ? FunnelTrack.Waitlist
              : context.funnelPacket?.utility.funnelTrack,
            promoCode:
              context.unparsedQueryParams?.promoCode ||
              context.queryParams?.promoCode ||
              context.funnelPacket?.user.promoCode,
            state: context.funnelPacket?.utility.state.abbreviation,
          });
        },
        saveStripePaymentIds: assign((_context, event) => ({
          stripePaymentId: event.data.stripePaymentId,
          stripeCustomerId: event.data.stripeCustomerId,
        })),
        saveFunnelPacket: assign((context, event) => {
          const eventUnparsedQueryParams = {
            utilityAccountNumber:
              event.data.funnelPacket.unparsedQueryParams?.utilityAccountNumber ?? undefined,
            secondaryAccountId:
              event.data.funnelPacket.unparsedQueryParams?.secondaryAccountId ?? undefined,
            meterNumber: event.data.funnelPacket.unparsedQueryParams?.meterNumber ?? undefined,
            annualizedUsageEstimate:
              event.data.funnelPacket.unparsedQueryParams?.annualizedUsageEstimate ?? undefined,
          };

          const mergedUnparsedQueryParams = {
            ...context.queryParams?.unparsedQueryParams,
            ...eventUnparsedQueryParams,
          };

          const updatedQueryParams = {
            ...context.queryParams,
            unparsedQueryParams: mergedUnparsedQueryParams,
          };

          return {
            ...context,
            queryParams: updatedQueryParams,
            unparsedQueryParams: mergedUnparsedQueryParams,
            funnelPacket: event.data.funnelPacket,
          };
        }),
        saveUserSubmission: assign((context, event) => {
          const agentName = event.data.agentName! || context.agentName!;
          const updatedUtmParams = agentName
            ? { ...context.utmParams, utm_agent: agentName }
            : context.utmParams;
          const updatedUnparsedQueryParams = {
            ...context.unparsedQueryParams,
            ...context.utmParams,
            utm_agent: agentName,
          };
          const updatedQueryParams = {
            ...context.queryParams,
            utmParams: updatedUtmParams,
            unparsedQueryParams: updatedUnparsedQueryParams,
          };
          return {
            agentName,
            businessName: event.data.businessName,
            email: event.data.email,
            firstName: event.data.firstName,
            inviteCode: event.data.inviteCode,
            ipAddress: event.data.ipAddress,
            generalContractIds: event.data.generalContractIds,
            lastName: event.data.lastName,
            password: event.data.password,
            phone: event.data.phone,
            optInToSMS: event.data.optInToSMS,
            budgetBilling: event.data.budgetBilling,
            utmParams: updatedUtmParams,
            unparsedQueryParams: updatedUnparsedQueryParams,
            queryParams: updatedQueryParams,
          };
        }),
        setGoogleError: assign((context, event) => ({
          errors: setError<UserStates>('google', context, event),
        })),
        setUserError: assign((context, event) => ({
          errors: setError<UserStates>('user', context, event),
        })),
        trackOptInToSms: context => {
          if (context.optInToSMS) {
            sendToSegment({
              type: 'track_sms_opt_in_preference',
              value: context.optInToSMS,
              userId: context.funnelPacket?.user.id,
            });
          }
        },
        trackCompleted: async context => {
          /* We are fetching the IP again as we don't want to include
           * the user's IP in the funnel packet.
           */
          const userIp = getUserIpFromStore();
          sendToSegment({
            type: 'track_query_params',
            funnelPacket: context.funnelPacket!,
            userIp,
            queryParams: context.queryParams!,
          });
          if (!context.inviteCode) {
            sendToSegment({
              type: 'user_step_completed',
              userIp,
              utilityName: context.utility.displayName,
              zipCode: context.zipCode,
              state: context.utility.state.name,
              funnelPacket: context.funnelPacket!,
            });
          }
        },
        trackStarted: context => {
          sendToSegment({
            type: 'user_step_started',
            utilityId: context.utility.id,
            funnelTrack: context.utility.funnelTrack,
          });
        },
        trackScreen1: context => {
          sendToSegment({
            type: 'track_micro_learning_screen',
            screen: 'getStarted',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackNextButtonPressedScreen1: context => {
          sendToSegment({
            type: 'track_next_button_pressed',
            screen: 'getStarted',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackSkipButtonPressedScreen1: context => {
          sendToSegment({
            type: 'track_skip_button_pressed',
            screen: 'getStarted',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackScreen2: context => {
          sendToSegment({
            type: 'track_micro_learning_screen',
            screen: 'solarWithoutARoof',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackNextButtonPressedScreen2: context => {
          sendToSegment({
            type: 'track_next_button_pressed',
            screen: 'solarWithoutARoof',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackSkipButtonPressedScreen2: context => {
          sendToSegment({
            type: 'track_skip_button_pressed',
            screen: 'solarWithoutARoof',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackScreen3: context => {
          sendToSegment({
            type: 'track_micro_learning_screen',
            screen: 'howItWorks',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackNextButtonPressedScreen3: context => {
          sendToSegment({
            type: 'track_next_button_pressed',
            screen: 'howItWorks',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackSkipButtonPressedScreen3: context => {
          sendToSegment({
            type: 'track_skip_button_pressed',
            screen: 'howItWorks',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackScreen4: context => {
          sendToSegment({
            type: 'track_micro_learning_screen',
            screen: 'readyToSubscribe',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackDoneButtonPressedScreen4: context => {
          sendToSegment({
            type: 'track_done_button_pressed',
            screen: 'readyToSubscribe',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
        trackSkipButtonPressedScreen4: context => {
          sendToSegment({
            type: 'track_skip_button_pressed',
            screen: 'readyToSubscribe',
            utilityId: context.utility.id,
            zipCode: context.zipCode,
          });
        },
      },
      /* eslint-disable @typescript-eslint/no-unused-vars */
      guards: {
        hasReturnedFromGoogle: context => !!context.googleUser,
        shouldViewMicroLearning: context => {
          const resuming = !!context.resumingUser;
          const flagEnabled = LDClient.variation(LDFlags.showMicroLearningScreens);
          return !resuming && flagEnabled;
        },
      },
      services: {
        createUser: async context => {
          const userIp = getUserIpFromStore();
          /**
           * This powersetter specific code
           * probably shouldn't be written here.
           * Although, we are in a specific sceneario,
           * where we don't need the promocode to trigger
           * the start of powersetter flow, but still need
           * it when we create the user as `referralCode`
           *
           * It is very clumsy to override a promocode in
           * solar machine because of how complex the ContextCreation
           * function is. since this is only used by this service,
           * it makes it very easy to do this here.
           *
           * Ideally, there should be some clarity in the architecture
           * on how we treat referralCodes in the createUser API call
           * as going forward, they are not just sourced from query params.
           */
          const powerSetterPromoCode = isPowerSetter(context)
            ? PROMO_CODES.POWER_SETTER_DATA_ENTRY
            : undefined;
          const promoCode = context.promoCode || powerSetterPromoCode;
          const createUserParams: CreateUserParams = {
            promoCode,
            budgetBilling: context.budgetBilling,
            businessName: context.businessName,
            email: context.email!,
            firstName: context.firstName!,
            funnelId: context.funnelId,
            inviteCode: context.inviteCode,
            ipAddress: userIp,
            generalContractIds: context.generalContractIds,
            lastName: context.lastName!,
            originationSource: context.originationSource,
            password: context.password,
            smsOptIn: context.optInToSMS,
            funnelTrack: context.utility.funnelTrack,
            phone: context.phone,
            funnel: context.unparsedQueryParams?.funnel,
            planTemplateId: 609, // placeholder plan template
            unparsedQueryParams: context.unparsedQueryParams,
            utilityId: context.utility.id,
            utmParams: context.utmParams,
            zipCode: context.zipCode,
            metaData: JSON.stringify(store.get('META_DATA')),
          };
          if (context.isMinnesotaLMI) {
            createUserParams.funnelVnmProjectId =
              context.funnelVnmProjectId! ||
              context.funnelPacket?.funnelVnmProjectId! ||
              LDClient.variation(LDFlags.ffDefaultSignupProjectId)?.project_id;
          }
          if (context?.queryParams?.promoCode === PROMO_CODES.ENERGY_WELL_SIGNUP) {
            createUserParams.stripeCustomerId = context.stripeCustomerId;
            createUserParams.stripePaymentId = context.stripePaymentId;
          }
          const { userId } = await createUser(createUserParams);

          if (context.googleUser) {
            await updateUserPreferences({ enabledGoogleSignIn: true, userId });
          }

          const funnelPacket = await fetchFunnelPacket({ userId });
          return { funnelPacket };
        },
        updateFunnelStatus: async context => {
          const promoCode =
            context.queryParams?.promoCode ||
            context.funnelPacket?.user.signupPromoCode?.code ||
            context.funnelPacket?.user.promoCode;
          if (allowConsolidatedUtilityAccount(promoCode)) {
            const funnelStatus = context.inviteCode
              ? 'Account Created with Invite Code'
              : 'Account Created';
            const utilityAccountId = context.funnelPacket?.utilityAccount.id!;
            const updateInput = {
              funnelStatus,
              ipAddress: getUserIpFromStore(),
            };
            await updateConsolidatedUtilityAccount(utilityAccountId, updateInput);
          }
        },
      },
    },
  );

export const evaluateUserFlag = (
  context: UserContext,
  flagName:
    | 'disableEmail'
    | 'hideGoogleButton'
    | 'requireBusinessName'
    | 'requirePhone'
    | 'hidePassword'
    | 'hideTerms'
    | 'showPhone'
    | 'showStripeIdInputs'
    | 'showInviteCode'
    | 'isInviteCodeRequired'
    | 'showBillingCheckbox',
): boolean => {
  const flag = context[flagName];
  return isFunction(flag) ? flag(context) : flag;
};

export const userContext = contextCreator<UserContext, 'utility' | 'zipCode'>(
  userMachine,
  queryParams => {
    const agentName: string | undefined = queryParams.utmParams?.utm_agent?.toString();
    const updatedUtmParams = agentName
      ? { ...queryParams.utmParams, utm_agent: agentName }
      : queryParams.utmParams;
    const updatedUnparsedQueryParams = agentName
      ? { ...queryParams.unparsedQueryParams, utm_agent: agentName }
      : queryParams.unparsedQueryParams;
    const updatedQueryParams = {
      ...queryParams,
      utmParams: updatedUtmParams,
      unparsedQueryParams: updatedUnparsedQueryParams,
    };

    return {
      agentName,
      disableEmail: !!queryParams.email,
      hideGoogleButton: (context: UserContext) => {
        const { promoCode, queryParams } = context;
        return (
          promoCode === PROMO_CODES.ENERGY_WELL_SIGNUP ||
          !!queryParams?.email ||
          isPowerSetter(context)
        );
      },
      hideTerms: (context: UserContext) => {
        const { promoCode } = context;
        return promoCode === PROMO_CODES.ENERGY_WELL_SIGNUP || isPowerSetter(context);
      },
      isInviteCodeRequired: (context: UserContext) => {
        const { promoCode } = context;
        return promoCode === PROMO_CODES.ENERGY_WELL_SIGNUP || isPowerSetter(context);
      },
      showStripeIdInputs: ({ promoCode }: UserContext) =>
        promoCode === PROMO_CODES.ENERGY_WELL_SIGNUP,
      showBillingCheckbox: (context: UserContext) =>
        UTILITY_IDS['Dominion Energy VA'] === context.utility.id, // Dominian VA
      email: queryParams.email,
      firstName: queryParams.firstName,
      lastName: queryParams.lastName,
      inviteCode: queryParams.inviteCode,
      googleUser: queryParams.googleUser,
      googleUserError: queryParams.googleUserError,
      phone: queryParams.phone,
      promoCode: queryParams.promoCode,
      businessName: queryParams.businessName,
      unparsedQueryParams: updatedUnparsedQueryParams,
      utmParams: updatedUtmParams,
      queryParams: updatedQueryParams,
    };
  },
);
